import React, {
  useState,
  useEffect,
  Fragment,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Menu,
  Snackbar,
  Alert,
} from "@mui/material";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { useAuth } from "context/AuthContext";
import { formatCurrency, formatDate } from "utils/formatters";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import ProjectToolbar from "../projects/ProjectToolbar";
import AddContractModal from "./AddContractModal";
import "./ProjectList.css";

const ProjectStatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    // Make sure the status handling is case-insensitive
    const statusLower = status ? status.toLowerCase() : "";

    // Map of status values to colors
    const colors = {
      "chưa bắt đầu": "var(--status-pending)",
      "đang thực hiện": "var(--status-in-progress)",
      "hoàn thành": "var(--status-completed)",
      "tạm dừng": "var(--status-on-hold)",
      "hủy bỏ": "var(--status-cancelled)",
      // English equivalents (in case backend returns English)
      "not started": "var(--status-pending)",
      "in progress": "var(--status-in-progress)",
      completed: "var(--status-completed)",
      "on hold": "var(--status-on-hold)",
      cancelled: "var(--status-cancelled)",
    };

    return colors[statusLower] || "var(--status-default)";
  };

  return (
    <span
      className="status-badge"
      style={{ backgroundColor: getStatusColor(status) }}
    >
      {status || "Chưa xác định"}
    </span>
  );
};

const ProjectCard = ({ project, onProjectInfoClick, onPermissionError }) => {
  // Calculate the duration in days between signing date and end date
  const calculateDuration = (signDate, endDate) => {
    if (!signDate || !endDate) return "Chưa xác định";

    const signDateObj = new Date(signDate);
    const endDateObj = new Date(endDate);

    // Calculate the time difference in milliseconds
    const timeDiff = Math.abs(endDateObj.getTime() - signDateObj.getTime());

    // Convert the time difference to days
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return `${daysDiff} ngày`;
  };

  // State for menu anchor
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Handle menu open
  const handleMenuClick = (event) => {
    event.stopPropagation(); // Prevent card click event
    setMenuAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchorEl(null);
  };

  // Handle project info click
  const handleProjectInfoClick = (event) => {
    event.stopPropagation();
    handleMenuClose(event);
    onProjectInfoClick(project.contractId);
  };

  // Handle project update click
  const handleProjectUpdateClick = async (event) => {
    event.stopPropagation();
    handleMenuClose(event);

    // Kiểm tra quyền cập nhật hợp đồng
    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_UPDATE);
    if (result.hasPermission) {
      // Mở modal chỉnh sửa hợp đồng
      if (typeof window !== "undefined" && window.openContractEditModal) {
        window.openContractEditModal(project.contractId);
      }
    } else {
      // Gọi callback để hiển thị thông báo lỗi quyền
      if (onPermissionError) {
        onPermissionError("Bạn không có quyền cập nhật hợp đồng.");
      }
    }
  };

  // Get the appropriate end date (actual or planned)
  const actualEndDate = project.endDateAct || project.endDate;

  return (
    <div className="project-card">
      <div className="project-card-heading">
        <div className="project-card-header">
          <div className="project-contract-info">
            <div className="contract-id">
              <span className="label">Số hợp đồng:</span>
              <span className="value">
                {project.contractNumber || "Chưa xác định"}
              </span>
            </div>
            <h3 className="project-name">{project.contractName}</h3>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Tùy chọn">
              <IconButton onClick={handleMenuClick} size="small" sx={{ mr: 1 }}>
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
            >
              <MenuItem onClick={handleProjectInfoClick}>
                Thông tin hợp đồng
              </MenuItem>
              <MenuItem onClick={handleProjectUpdateClick}>
                Cập nhật hợp đồng
              </MenuItem>
            </Menu>
            <div className="project-status-badge">
              <ProjectStatusBadge status={project.status} />
            </div>
          </div>
        </div>

        <div className="project-timeline-info">
          <div className="project-timeline">
            <span className="info-label">Thời hạn hợp đồng</span>
            <div className="timeline-values">
              <span className="time-value">
                {calculateDuration(project.signDate, actualEndDate)}
              </span>
              <span className="date-value">
                từ {formatDate(project.signDate)} đến{" "}
                {formatDate(actualEndDate)}
              </span>
            </div>
          </div>

          <div className="project-type">
            <span className="info-label">Phạm vi</span>
            <span className="type-value">
              {project.scope || "Chưa xác định"}
            </span>
          </div>
        </div>
      </div>{" "}
      <div className="project-body">
        <div className="bidder-info">
          <div className="info-item">
            <span className="label">Chủ đầu tư:</span>
            <span className="value">
              {project.clientName || "Chưa xác định"}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Đơn vị thi công:</span>
            <span className="value">
              {project.contractorName || "Chưa xác định"}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Giá trị:</span>
            <span className="value">
              {formatCurrency(project.totalValue).replace(/\s*₫\s*$/, "")}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Nhóm hợp đồng:</span>
            <span className="value">
              {project.contractGroup || "Chưa xác định"}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Ngày ký:</span>
            <span className="value">{formatDate(project.signDate)}</span>
          </div>
          <div className="info-item">
            <span className="label">Ngày bắt đầu:</span>
            <span className="value">
              {formatDate(project.startDateAct || project.startDate)}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Ngày kết thúc:</span>
            <span className="value">{formatDate(actualEndDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ searchTerm, statusFilter }) => {
  let message = "Không có hợp đồng nào để hiển thị";

  if (searchTerm) {
    message = `Không tìm thấy hợp đồng nào khớp với từ khóa "${searchTerm}"`;
  } else if (statusFilter && statusFilter !== "Tất cả hợp đồng") {
    message = `Không có hợp đồng nào có trạng thái "${statusFilter}"`;
  }

  return (
    <Box className="empty-state">
      <span className="material-icons">search</span>
      <Typography variant="h6">{message}</Typography>{" "}
      <Typography variant="body2">
        Thử thay đổi bộ lọc hoặc tạo hợp đồng mới
      </Typography>
    </Box>
  );
};

const ProjectTable = ({
  projects,
  sortColumn,
  sortDirection,
  onSortChange,
  onRowClick,
}) => {
  const progress = 65; // Temporary hardcoded progress

  const handleSortChange = (column) => {
    const isAsc = sortColumn === column && sortDirection === "ASC";
    onSortChange(column, isAsc ? "DESC" : "ASC");
  };

  // Column mapping for the backend
  const columnMapping = {
    contractName: "ContractName",
    contractNumber: "ContractNumber",
    clientName: "ClientName",
    contractorName: "ContractorName",
    contractGroup: "ContractGroup",
    totalValue: "TotalValue",
    startDateAct: "StartDateAct",
    endDate: "EndDate",
    status: "Status",
  };

  if (projects.length === 0) {
    return null;
  }

  return (
    <TableContainer component={Paper} className="project-table-container">
      <Table sx={{ minWidth: 650 }} aria-label="project table">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.contractName}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.contractName)}
              >
                Tên hợp đồng
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.contractNumber}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.contractNumber)}
              >
                Số hợp đồng
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.clientName}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.clientName)}
              >
                Chủ đầu tư
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.contractorName}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.contractorName)}
              >
                Đơn vị thi công
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.contractGroup}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.contractGroup)}
              >
                Nhóm hợp đồng
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.totalValue}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.totalValue)}
              >
                Giá trị
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.startDateAct}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.startDateAct)}
              >
                Ngày bắt đầu
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.endDate}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.endDate)}
              >
                Ngày kết thúc
              </TableSortLabel>
            </TableCell>
            <TableCell>Tiến độ</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortColumn === columnMapping.status}
                direction={sortDirection.toLowerCase()}
                onClick={() => handleSortChange(columnMapping.status)}
              >
                Trạng thái
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => (
            <TableRow
              key={project.contractId}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              className="project-table-row"
              onClick={() => onRowClick(project.contractId)}
              hover
            >
              <TableCell
                component="th"
                scope="row"
                className="table-cell-contract-name"
              >
                <span className="contract-name-cell">
                  {project.contractName}
                </span>
              </TableCell>
              <TableCell>{project.contractNumber}</TableCell>
              <TableCell>{project.clientName}</TableCell>
              <TableCell>{project.contractorName}</TableCell>
              <TableCell>{project.contractGroup || ""}</TableCell>
              <TableCell>
                {formatCurrency(project.totalValue).replace(/\s*₫\s*$/, "")}
              </TableCell>
              <TableCell>{formatDate(project.startDateAct)}</TableCell>
              <TableCell>{formatDate(project.endDate)}</TableCell>
              <TableCell>
                <div className="progress-cell">
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="progress-text">{progress}%</span>
                </div>
              </TableCell>
              <TableCell>
                <ProjectStatusBadge status={project.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ProjectList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // Changed default to list view
  const [authMessage, setAuthMessage] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("SignDate");
  const [sortDirection, setSortDirection] = useState("DESC");
  const [statusFilter, setStatusFilter] = useState("Tất cả hợp đồng");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContractId, setEditContractId] = useState(null);

  // Permission Snackbar state
  const [permissionSnackbarOpen, setPermissionSnackbarOpen] = useState(false);
  const [permissionSnackbarMessage, setPermissionSnackbarMessage] =
    useState("");
  const [permissionSnackbarSeverity, setPermissionSnackbarSeverity] =
    useState("warning");

  // Contract filtering states
  const [allowedContractIds, setAllowedContractIds] = useState(null);
  const [contractFilterError, setContractFilterError] = useState(null);

  // Cache for user contract permissions (using useRef to avoid re-renders)
  const userContractCacheRef = useRef(null);

  // Check if user is admin
  const isAdmin = user?.roleName === "Admin" || user?.role === "Admin";



  // Function to get user contract permissions
  const getUserContractPermissions = useCallback(
    async (userId) => {
      try {
        setContractFilterError(null);

        // If admin, allow all contracts
        if (isAdmin) {
          setAllowedContractIds("all");
          return;
        }

        // Check cache first
        if (userContractCacheRef.current) {
         
          const contractIds = userContractCacheRef.current.map(
            (uc) => uc.contractId
          );
          setAllowedContractIds(contractIds);
          return;
        }

        // Get user contracts (request all contracts without pagination limit)
        const userContractsResponse = await api.get(
          `/UserContract/user/${userId}?pageSize=1000`
        );

        if (
          userContractsResponse.data.success &&
          userContractsResponse.data.data?.length > 0
        ) {
          // User has specific contracts assigned
          const contractIds = userContractsResponse.data.data.map(
            (uc) => uc.contractId
          );
 
          userContractCacheRef.current = userContractsResponse.data.data;
          setAllowedContractIds(contractIds);
         
        } else {
          // No user contracts found - user has no assigned contracts
       
          setAllowedContractIds("none");
        }

      
      } catch (error) {
        console.error("Error getting user contract permissions:", error);
        setContractFilterError("Không thể hiển thị danh sách hợp đồng");
        setAllowedContractIds("error");
      }
    },
    [isAdmin]
  );

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(1); // Reset về trang 1 khi tìm kiếm
  };
  const handleStatusChange = (status) => {
    
    setStatusFilter(status);
    setPage(1); // Reset về trang 1 khi đổi status
  };

  const handleSortChange = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };
  const handleRowClick = (contractId) => {
    // Navigate to project detail page
    navigate(`/contracts/${contractId}`);
  };

  // Handle project info click from card menu
  const handleProjectInfoClick = (contractId) => {
    navigate(`/contracts/${contractId}`);
  };

  // Handle add contract with permission check
  const handleAddContract = async () => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_CREATE);
    if (result.hasPermission) {
      setAddModalOpen(true);
    } else {
      setPermissionSnackbarMessage("Bạn không có quyền thêm hợp đồng.");
      setPermissionSnackbarSeverity("warning");
      setPermissionSnackbarOpen(true);
    }
  };

  // Handle permission error for project cards
  const handlePermissionError = (message) => {
    setPermissionSnackbarMessage(message);
    setPermissionSnackbarSeverity("warning");
    setPermissionSnackbarOpen(true);
  };

  // Load user contract permissions when user changes
  useEffect(() => {
    if (user?.userId) {
      // Clear cache and reset state when user changes
      userContractCacheRef.current = null;
      setAllowedContractIds(null); // Reset to trigger new fetch

      getUserContractPermissions(user.userId);
    }
  }, [user?.userId, getUserContractPermissions]);

  // Kiểm tra thông báo từ URL query parameter và xử lý
  useEffect(() => {
    // Hàm xử lý thông báo từ URL
    const handleUrlMessage = () => {
      // Đọc thông báo từ URL
      const queryParams = new URLSearchParams(window.location.search);
      const urlMessage = queryParams.get("message");
      const shouldShowSnackbar = queryParams.get("showSnackbar") === "true";



      if (urlMessage) {
        try {
          // Giải mã thông báo
          const decodedMessage = decodeURIComponent(urlMessage);


          // Đặt thông báo vào state
          setAuthMessage(decodedMessage);

          // Kiểm tra xem có nên hiển thị Snackbar không
          if (shouldShowSnackbar) {
            setShowSnackbar(true);
          }

          // Xóa query parameter sau khi đã lấy thông báo để URL gọn gàng
          try {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          } catch (e) {
            console.error("Error clearing URL query params:", e);
          }
        } catch (error) {
          console.error("Error processing URL message:", error);
        }
      }
    };

    // Gọi hàm xử lý
    handleUrlMessage();
  }, []); // Dependency array trống, useEffect chỉ chạy một lần khi mount

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);

        // Wait for contract permissions to be loaded
        if (allowedContractIds === null) {
          return;
        }

        // Handle contract filtering error
        if (allowedContractIds === "error") {
          setError(
            contractFilterError || "Không thể hiển thị danh sách hợp đồng"
          );
          setProjects([]);
          setTotalCount(0);
          setTotalPages(1);
          setLoading(false);
          return;
        }

        // Handle no contracts allowed
        if (allowedContractIds === "none") {
          setProjects([]);
          setTotalCount(0);
          setTotalPages(1);
          setError("Không có hợp đồng được phân quyền");
          setLoading(false);
          return;
        }

        // Process status filter properly
        let statusParam = null;
        if (
          statusFilter !== "Tất cả hợp đồng" &&
          statusFilter !== "Tất cả hợp đồng"
        ) {
          statusParam = statusFilter;
        }

        const params = {
          pageNumber: Number(page),
          pageSize: Number(itemsPerPage),
          searchTerm: searchTerm || "",
          sortColumn: sortColumn || "SignDate",
          sortDirection: sortDirection || "DESC",
          status: statusParam,
        };

        // Add contract filtering if not admin and not "all"
        if (allowedContractIds !== "all" && Array.isArray(allowedContractIds)) {
          // Add contractIds filter to params for backend filtering
          params.contractIds = allowedContractIds.join(",");

        } else {


        }

        const response = await api.get("/contract", {
          params: params,
        });




        if (response.data.success) {
          // Get the data from backend - backend đã filter theo contractIds
          let projectsData = response.data.data || [];


          setProjects(projectsData);

          // Use totalCount from backend for pagination
          const actualTotalCount = response.data.totalCount || 0;

          setTotalCount(actualTotalCount);
          setTotalPages(Math.ceil(actualTotalCount / itemsPerPage));
          setError(null);
        } else {
          setProjects([]);
          setError(response.data.message || "Lỗi khi tải dữ liệu hợp đồng");
        }
        setLoading(false);
      } catch (err) {
        console.error("API Error:", err);
        setProjects([]);

        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          let errorMessage = `Lỗi ${err.response.status}`;


          if (err.response.data) {
            if (typeof err.response.data === "string") {
              errorMessage += `: ${err.response.data}`;
            } else if (err.response.data.message) {
              errorMessage += `: ${err.response.data.message}`;
            } else if (err.response.statusText) {
              errorMessage += `: ${err.response.statusText}`;
            }
          }

          setError(errorMessage);
        } else if (err.request) {
          // The request was made but no response was received
          setError(
            "Không thể kết nối tới máy chủ. Vui lòng kiểm tra kết nối mạng của bạn."
          );
        } else {
          // Something happened in setting up the request that triggered an Error
          setError(`Lỗi: ${err.message}`);
        }

        setLoading(false);
      }
    };
    fetchProjects();
  }, [
    page,
    itemsPerPage,
    searchTerm,
    sortColumn,
    sortDirection,
    statusFilter,
    refreshFlag,
    allowedContractIds,
    contractFilterError,
    isAdmin,
    user,
  ]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    // Reset to first page when changing items per page
    setPage(1);
  };

  // Make the function available globally (for menu click handlers)
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.openContractEditModal = (openContractId) => {
        setEditContractId(openContractId);
        setEditModalOpen(true);
      };
    }

    return () => {
      if (typeof window !== "undefined") {
        delete window.openContractEditModal;
      }
    };
  }, []);

  if (loading) return <div className="loading">Đang tải...</div>;
  if (error)
    return (
      <div className="no-contracts-container">
        <div className="no-contracts-message">
          <div className="no-contracts-icon">
            <span className="material-icons">folder_open</span>
          </div>
          <div className="no-contracts-content">
            <h3>Không có dữ liệu</h3>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-button-modern"
            >
              <span className="material-icons">refresh</span>
              Thử lại
            </button>
          </div>
        </div>
      </div>
    );
  return (
    <>
      {/* Snackbar thay thế cho Windows alert */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="warning"
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          Không đủ quyền! {authMessage}
        </Alert>
      </Snackbar>

      {/* Permission Snackbar */}
      <Snackbar
        open={permissionSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setPermissionSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setPermissionSnackbarOpen(false)}
          severity={permissionSnackbarSeverity}
          sx={{ width: "100%" }}
        >
          {permissionSnackbarMessage}
        </Alert>
      </Snackbar>
      {/* Modal tạo mới hợp đồng */}
      <AddContractModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onContractAdded={() => {
          setAddModalOpen(false);
          setPage(1);
          setRefreshFlag((f) => !f); // Trigger refresh
        }}
      />

      {/* Modal chỉnh sửa hợp đồng */}
      <AddContractModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onContractAdded={() => {
          setEditModalOpen(false);
          setRefreshFlag((f) => !f); // Trigger refresh
        }}
        editMode={true}
        contractId={editContractId}
      />
      <div className="projects-container">
        <div className="fixed-header">
          <ProjectToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onSearch={handleSearch}
            onStatusChange={handleStatusChange}
            statusFilter={statusFilter}
            initialSearchTerm={searchTerm}
            onAddContract={handleAddContract}
          />
        </div>
        <div className="scrollable-content">
          {projects.length === 0 ? (
            <EmptyState searchTerm={searchTerm} statusFilter={statusFilter} />
          ) : (
            <>
              <div className="pagination-container">
                <div className="pagination-left">
                  {" "}
                  <FormControl className="items-per-page-container">
                    <div className="items-per-page-label">
                      Số hợp đồng/trang
                    </div>{" "}
                    <Select
                      id="items-per-page-select"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="items-per-page-select"
                      variant="standard"
                      disableUnderline
                      IconComponent={() => null}
                      renderValue={(value) => (
                        <Box className="select-dropdown">
                          <span className="selected-value">{value}</span>
                          <span className="material-icons dropdown-icon">
                            arrow_drop_down
                          </span>
                        </Box>
                      )}
                      MenuProps={{
                        className: "items-per-page-menu",
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "right",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "right",
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" className="total-count">
                    Tổng số: <strong>{totalCount}</strong> hợp đồng
                  </Typography>
                </div>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                />
              </div>
              {viewMode === "grid" ? (
                <div className="projects-grid">
                  {projects.map((project) => (
                    <div
                      key={project.contractId}
                      className="project-card-wrapper"
                      onClick={() => handleRowClick(project.contractId)}
                    >
                      <ProjectCard
                        project={project}
                        onProjectInfoClick={handleProjectInfoClick}
                        onPermissionError={handlePermissionError}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <ProjectTable
                  projects={projects}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSortChange={handleSortChange}
                  onRowClick={handleRowClick}
                />
              )}
              <div className="pagination-container bottom">
                <div className="pagination-simple">
                  <Typography variant="body2" className="page-info">
                    Trang {page} / {totalPages || 1}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectList;
